// Documents on file for a participant (grouped by visit; each can be linked to a visit later), an event or an organization
// (optionally with the people's documents from it): upload (PDF / JPEG / PNG, up to 25 MB), list, open, remove.
import { Fragment, useRef, useState, type FormEvent } from 'react';
import { get, isAdmin, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useData } from '../lib/useData.ts';
import { ErrorBox } from './Layout.tsx';

export const DOC_TYPE_LABEL: Record<string, string> = {
  lab_panel: 'Lab panel / blood work',
  drug_screen: 'Drug screen',
  ekg: 'EKG',
  pft: 'Spirometry / PFT',
  clearance: 'Clearance form (physician, fit for duty, respirator)',
  test_result: 'Other test result (hearing, vision, X-ray...)',
  authorization: 'Signed authorization / consent',
  medical_history: 'Medical history questionnaire',
  prior_report: 'Prior report',
  outside_record: 'Outside medical record',
  intake_form: 'Intake form (scan)',
  roster: 'Roster / sign-in sheet',
  correspondence: 'Letter / correspondence',
  follow_up: 'Follow-up / clearance record',
  clearance_letter: 'Clearance letter',
  other: 'Other',
};

interface DocRow {
  id: string; assessmentId: string | null; assessedOn: string | null; docType: string; restricted: boolean;
  originalName: string | null; contentType: string; sizeBytes: number; note: string | null; sha256: string;
  title: string | null; documentDate: string | null; eventId?: string | null; eventOn?: string | null; eventLocation?: string | null;
  departmentName?: string | null; visitType?: string | null; participantId?: string | null; participantName?: string | null;
  uploadedAt: string; uploadedBy: string | null; uploadedByName: string | null;
}

const size = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Documents of a person (participantId), an event (eventId: filed on the event, not on a person) or an organization
 *  (departmentId: its own and its events' documents). */
export interface VisitRef { id: string; assessedOn: string; status?: string; visitType?: string | null; departmentName?: string | null; supersededBy?: string | null }
const VISIT_LABEL: Record<string, string> = { physical: 'Physical', cohsg: 'COHSG screening', labs: 'Labs only' };
const us = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-').map(Number); return `${m}/${d}/${y}`; };
const visitName = (a: VisitRef) => [us(a.assessedOn), a.visitType ? VISIT_LABEL[a.visitType] : null, a.departmentName,
  a.status === 'void' ? 'void' : a.supersededBy ? 'replaced by a correction' : null].filter(Boolean).join(' · ');

export function DocumentsCard({ participantId, eventId, departmentId, assessments = [] }: {
  participantId?: string; eventId?: string; departmentId?: string; assessments?: VisitRef[];
}) {
  const { me } = useAuth();
  const base = participantId ? `/api/participants/${participantId}` : eventId ? `/api/events/${eventId}` : `/api/departments/${departmentId}`;
  const personal = !!participantId;
  const [withPeople, setWithPeople] = useState(false);
  const { data, error, reload } = useData<{ documents: DocRow[]; canSeeRestricted: boolean }>(`${base}/documents${!personal && withPeople ? '?people=1' : ''}`);
  const [adding, setAdding] = useState(false);
  const [docType, setDocType] = useState(participantId ? 'lab_panel' : 'roster');
  const [assessmentId, setAssessmentId] = useState('');
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [docDate, setDocDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setDone(null);
    const f = fileRef.current?.files?.[0];
    if (!f) return setErr(new Error('Choose a file to upload.'));
    if (f.size > 25 * 1024 * 1024) return setErr(new Error('That file is larger than 25 MB.'));
    const fd = new FormData();
    fd.append('docType', docType);   // fields before the file
    if (assessmentId) fd.append('assessmentId', assessmentId);
    if (note.trim()) fd.append('note', note.trim());
    if (title.trim()) fd.append('title', title.trim());
    if (docDate) fd.append('documentDate', docDate);
    fd.append('file', f, f.name);
    setBusy(true);
    try {
      const r = await post<{ restricted: boolean }>(`${base}/documents`, fd);
      setDone(r.restricted && !data?.canSeeRestricted
        ? `${f.name} uploaded. Drug screens are restricted, so it will not appear in your list; NGF staff with access can open it.`
        : `${f.name} uploaded.`);
      setAdding(false); setNote(''); setAssessmentId(''); setTitle(''); setDocDate('');
      if (fileRef.current) fileRef.current.value = '';
      await reload();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  const [moving, setMoving] = useState<string | null>(null);   // admins: document being moved to another person
  const linkVisit = async (d: DocRow, assessmentId: string) => {
    setErr(null); setDone(null);
    try { await post(`/api/documents/${d.id}/visit`, { assessmentId: assessmentId || null }); await reload(); } catch (x) { setErr(x); }
  };
  const remove = async (d: DocRow) => {
    const reason = window.prompt(`Remove "${d.title ?? d.originalName ?? DOC_TYPE_LABEL[d.docType]}"? It will be hidden (the file is kept for the record).\n\nReason:`);
    if (!reason) return;
    try { await post(`/api/documents/${d.id}/remove`, { reason }); await reload(); } catch (x) { setErr(x); }
  };

  const all = data?.documents ?? [];
  const docs = typeFilter ? all.filter((d) => d.docType === typeFilter) : all;
  const typesPresent = [...new Set(all.map((d) => d.docType))];
  // A person's documents are grouped by visit (newest first), then those not linked to a visit; elsewhere one list.
  const visitChoices = assessments.filter((a) => a.status !== 'void');
  const groups: { key: string; label: string | null; docs: DocRow[] }[] = !personal ? [{ key: 'all', label: null, docs }] : [
    ...assessments.map((a) => ({ key: a.id, label: `Visit ${visitName(a)}`, docs: docs.filter((d) => d.assessmentId === a.id) })),
    { key: 'none', label: 'Not linked to a visit', docs: docs.filter((d) => !d.assessmentId || !assessments.some((a) => a.id === d.assessmentId)) },
  ].filter((g) => g.docs.length);
  return (
    <div className="card">
      <div className="spread">
        <h2 style={{ margin: 0 }}>{personal ? 'Documents' : eventId ? 'Event documents' : 'Organization documents'}</h2>
        <div className="actions">
          {!personal && <label className="small"><input type="checkbox" checked={withPeople} onChange={(e) => setWithPeople(e.target.checked)} />
            {' '}Include people's documents</label>}
          {!adding && <button className="secondary" onClick={() => { setAdding(true); setDone(null); }}>Upload document</button>}
        </div>
      </div>
      <ErrorBox error={error ?? err} />
      {done && <div className="alert ok" style={{ marginTop: '0.75rem' }}>{done}</div>}
      {adding && (
        <form onSubmit={upload} className="upload-form">
          <div className="row">
            <div className="field"><label htmlFor="doc-type">Type</label>
              <select id="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)}>
                {Object.entries(DOC_TYPE_LABEL).filter(([k]) => personal || !['drug_screen', 'intake_form'].includes(k)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            {personal && <div className="field"><label htmlFor="doc-asmt">For visit (optional)</label>
              <select id="doc-asmt" value={assessmentId} onChange={(e) => setAssessmentId(e.target.value)}>
                <option value="">Not linked</option>
                {assessments.filter((a) => a.status !== 'void').slice(0, 30).map((a) => <option key={a.id} value={a.id}>{visitName(a)}</option>)}
              </select></div>}
          </div>
          <div className="field"><label htmlFor="doc-file">File</label>
            <input id="doc-file" ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" required />
            <div className="hint">PDF, JPEG or PNG, up to 25 MB. On an iPad you can take a photo of the page.</div></div>
          <div className="row">
            <div className="field"><label htmlFor="doc-title">Title (optional)</label>
              <input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Lipid panel - LabCorp" /></div>
            <div className="field" style={{ flex: '0 1 180px' }}><label htmlFor="doc-date">Document date (optional)</label>
              <input id="doc-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></div>
          </div>
          <div className="field"><label htmlFor="doc-note">Note (optional)</label>
            <input id="doc-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="e.g. Quest panel, fasting" /></div>
          {docType === 'drug_screen' && <div className="alert info small">Drug screens are restricted: NGF staff only, never shown to organizations.</div>}
          <div className="actions">
            <button disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button>
            <button type="button" className="secondary" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}
      {typesPresent.length > 1 && (
        <div className="field" style={{ maxWidth: 280, marginTop: '0.75rem' }}><label htmlFor="doc-filter">Show</label>
          <select id="doc-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All documents ({all.length})</option>
            {typesPresent.map((t) => <option key={t} value={t}>{DOC_TYPE_LABEL[t] ?? t} ({all.filter((d) => d.docType === t).length})</option>)}
          </select></div>)}
      {!data ? <p className="muted">Loading…</p> : !docs.length ? <p className="muted" style={{ marginTop: '0.75rem' }}>No documents on file.</p> : (
        <table style={{ marginTop: '0.75rem' }}>
          <thead><tr><th>Type</th><th>Document</th><th>Dated</th><th>{personal ? 'Visit' : withPeople ? 'Person · event' : 'Event'}</th><th>Added</th><th /></tr></thead>
          <tbody>{groups.map((g) => <Fragment key={g.key}>
            {g.label && <tr className="doc-group"><td colSpan={6}><b>{g.label}</b> <span className="muted small">({g.docs.length})</span></td></tr>}
            {g.docs.map((d) => ([
            <tr key={d.id}>
              <td>{DOC_TYPE_LABEL[d.docType] ?? d.docType}{d.restricted && <span className="badge provisional" style={{ marginLeft: 6 }}>restricted</span>}</td>
              <td><a href={`/api/documents/${d.id}/file`} target="_blank" rel="noopener">{d.title ?? d.originalName ?? 'document'}</a>
                <div className="muted small">{d.title && d.originalName ? `${d.originalName} · ` : ''}{size(d.sizeBytes)} · <a href={`/api/documents/${d.id}/file?download=1`}>download</a></div>
                {d.note && <div className="muted small">{d.note}</div>}</td>
              <td className="small">{d.documentDate ?? '—'}</td>
              <td>{personal ? (d.docType === 'intake_form' || !visitChoices.length ? (d.assessedOn ?? '—')
                  : <select aria-label="Visit" value={d.assessmentId ?? ''} onChange={(e) => void linkVisit(d, e.target.value)}>
                      <option value="">Not linked</option>
                      {visitChoices.map((a) => <option key={a.id} value={a.id}>{visitName(a)}</option>)}
                    </select>)
                : <>{d.participantId && <div><a href={`/participants/${d.participantId}`}>{d.participantName ?? 'Person'}</a></div>}
                    {d.eventOn ? <a href={`/events/${d.eventId}`}>{d.eventOn}</a> : d.participantId ? '' : 'Organization'}</>}</td>
              <td className="small">{new Date(d.uploadedAt).toLocaleString()}<div className="muted">{d.uploadedByName ?? ''}</div></td>
              <td>{me && (isAdmin(me.role) || d.uploadedBy === me.id) &&
                <button className="link" onClick={() => void remove(d)}>Remove</button>}
                {me && isAdmin(me.role) && personal && d.docType !== 'intake_form' &&
                  <> <button className="link" onClick={() => setMoving(moving === d.id ? null : d.id)}>Move</button></>}</td>
            </tr>,
            moving === d.id && <tr key={`${d.id}-move`}><td colSpan={6}><MoveDocument doc={d} onDone={() => { setMoving(null); void reload(); }} onCancel={() => setMoving(null)} /></td></tr>,
            ]))}</Fragment>)}</tbody>
        </table>
      )}
      {data && !data.canSeeRestricted && <p className="muted small" style={{ marginTop: '0.5rem' }}>Drug screens are visible to NGF technicians, physicians and admins only.</p>}
    </div>
  );
}

interface Found { id: string; firstName: string; lastName: string; dob: string | null; employeeId: string | null; departmentName: string | null }

/** Admins: put a document filed on the wrong person on the right one (audited, with a reason). */
function MoveDocument({ doc, onDone, onCancel }: { doc: DocRow; onDone: () => void; onCancel: () => void }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found[]>([]);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('Filed on the wrong person');
  const [err, setErr] = useState<unknown>(null);
  const search = async () => {
    setErr(null);
    try { setFound(await get<Found[]>(`/api/participants?limit=20&q=${encodeURIComponent(q.trim())}`)); } catch (x) { setErr(x); }
  };
  const move = async () => {
    const who = found.find((f) => f.id === to);
    if (!who) return setErr(new Error('Choose the person it belongs to.'));
    if (!window.confirm(`Move "${doc.title ?? doc.originalName ?? 'this document'}" to ${who.firstName} ${who.lastName}?`)) return;
    setErr(null);
    try { await post(`/api/documents/${doc.id}/move`, { participantId: to, reason: reason.trim() }); onDone(); } catch (x) { setErr(x); }
  };
  return (
    <div className="move-doc">
      <b>Move to another person</b>
      <ErrorBox error={err} />
      <div className="row" style={{ alignItems: 'end' }}>
        <div className="field"><label htmlFor={`mv-q-${doc.id}`}>Find the person</label>
          <input id={`mv-q-${doc.id}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or employee ID"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }} /></div>
        <div style={{ flex: '0 0 auto' }}><button type="button" className="secondary" disabled={!q.trim()} onClick={() => void search()}>Search</button></div>
      </div>
      {found.map((f) => (
        <label key={f.id} className="small" style={{ display: 'block' }}>
          <input type="radio" checked={to === f.id} onChange={() => setTo(f.id)} /> <strong>{f.lastName}, {f.firstName}</strong>
          {' '}· DOB {f.dob ?? '—'} · ID {f.employeeId ?? '—'} · {f.departmentName ?? 'No organization'}
        </label>))}
      <div className="field" style={{ marginTop: 6 }}><label htmlFor={`mv-r-${doc.id}`}>Reason</label>
        <input id={`mv-r-${doc.id}`} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} /></div>
      <div className="actions"><button type="button" disabled={!to || reason.trim().length < 3} onClick={() => void move()}>Move</button>
        <button type="button" className="secondary" onClick={onCancel}>Cancel</button></div>
    </div>
  );
}
